// playClient.ts - typed client for the public play surface endpoints.
// These endpoints are unauthenticated - every read below uses apiGetPublic,
// which never attaches the signed-in creator's own Cognito session token
// (see services/api.ts's own doc comment on why that distinction is real,
// not cosmetic: found during the play-origin-isolation audit,
// docs/editor-consolidation/play-origin-isolation.md - this file had been
// using the authenticated apiGet all along despite this very comment already
// claiming otherwise). apiPost stays for redirectToExternalPlay below,
// which is deliberately different: it bridges an already-signed-in visitor's
// real session to an external site, not a public data read.
import type { CharacterDefinition, CharacterPose, MapDocument, StudioProjectConfig } from '../../engine';
import { type AssetWire } from '../persistence/projectRepository';
import type { ModeDocKind } from '../services/docs';
import { apiGetPublic, apiPost } from '../services/api';
import type { GameMedia } from '../showcase/media';

/**
 * Wire shape of `GET /play/{slug}`. The api returns slug, visibility, version
 * and map_data today; name, updated_at, config and dev are optional extensions
 * requested cross-lane (ROADMAP, 2026-06-10) and bind automatically once the
 * api starts returning them.
 */
export interface PlayPayload {
  slug: string;
  visibility: 'public' | 'unlisted';
  version: number;
  // Absent when the publication is external (see external_url below) - a
  // redirect target carries no map data of its own.
  map_data?: MapDocument;
  name?: string;
  updated_at?: string | null;
  config?: Partial<StudioProjectConfig> | null;
  /** is_original is true when the publication's tenant matches the api's
   * configured "Soulstack Originals" tenant (settings.originals_tenant_id) -
   * a first-party flag, not a string-match against the mutable studio_name/
   * slug fields. Absent on an older api response; treat as falsy. */
  dev?: { slug: string; studio_name: string; is_original?: boolean } | null;
  /** The tenant's player pool id for this publication (app/routers/public.py's
   * ensure_game) - always present once a version exists. Player-tier auth
   * (signup/login, and the multiplayer relay token) is scoped to this id,
   * not the project id. */
  game_id?: string;
  /** Set when this publication redirects to its own dedicated site instead
   * of being rendered by the generic hosted-game template. */
  external_url?: string | null;
  /** Curated showcase media, same shape and caveats as `PublicGame.media`. */
  media?: GameMedia | null;
  /** Set only when the project has a *public* wiki - an unlisted one is
   * never surfaced here, by design (see the api). */
  wiki?: { slug: string; title: string; url: string } | null;
  /** The project's own custom characters/rigs beyond the built-in neutral
   * biped (mirrors the authenticated `/characters/roster`'s `characters`
   * field). A placed character resolves against this list the same way
   * Studio's own Edit/Preview does - see projectRepository's
   * `charactersFromRoster`. */
  characters?: CharacterDefinition[];
  /** Every authored pose saved for the project (mirrors the authenticated
   * `GET /characters/poses`), flat and ungrouped - see projectRepository's
   * `poseLibrariesFromSavedPoses` to fold these into the
   * `CharacterPoseLibrary[]` shape `<GameRuntime>` expects. */
  poses?: CharacterPose[];
}

/** Fetch the live published version for a slug. 404s reject. */
export function getPlay(slug: string): Promise<PlayPayload> {
  return apiGetPublic<PlayPayload>(`/play/${encodeURIComponent(slug)}`);
}

/**
 * Sends a signed-in player through to a bespoke game's own `external_url`
 * already carrying a real session, via the Cognito player bridge (POST
 * /players/link; see sstk-furrow's docs/production/decisions.md for the
 * full design). A guest visitor (no Cognito session, or the link call
 * fails for any reason -- expired session, the game not yet registered in
 * the bridge, a network error) always falls back to the bare `external_url`
 * redirect this replaces: bridging is a login shortcut, never a gate on
 * play, exactly like every bespoke game's existing "signed out is a valid
 * way to play" posture.
 */
export async function redirectToExternalPlay(slug: string, externalUrl: string): Promise<void> {
  try {
    const { code } = await apiPost<{ code: string }>('/players/link', { game_slug: slug });
    const target = new URL('/auth/callback', externalUrl);
    target.searchParams.set('code', code);
    window.location.replace(target.toString());
  } catch {
    window.location.replace(externalUrl);
  }
}

/** One entry in the public game listing, the wire shape of `GET /play`. */
export interface PublicGame {
  slug: string;
  name: string;
  external_url: string | null;
  updated_at: string | null;
  dev: { slug: string; studio_name: string; is_original?: boolean } | null;
  wiki: { slug: string; url: string } | null;
  /** Curated showcase media (api migration 0029): cover, screenshots,
   *  trailer, tagline and tags. Null until someone curates it, and absent
   *  entirely on an api older than that migration. */
  media?: GameMedia | null;
}

/** Every publicly visible publication, most recently published first. */
export function listPublicGames(): Promise<PublicGame[]> {
  return apiGetPublic<PublicGame[]>('/play');
}

/**
 * One aggregated leaderboard row, `GET /play/{slug}/leaderboard`'s wire
 * shape: public, rate limited, visibility enforced, mirroring the internal
 * `GET /games/{id}/leaderboard` rows ordered by wins, then kills, then
 * score. A publication with no game pool or no recorded matches returns an
 * empty array rather than 404 - callers treat a failed fetch the same way.
 */
export interface LeaderboardRow {
  player_ref: string;
  name: string | null;
  matches: number;
  wins: number;
  kills: number;
  deaths: number;
  assists: number;
  score: number;
}

/** Fetch a publication's public leaderboard. */
export function getPlayLeaderboard(slug: string, limit = 10): Promise<LeaderboardRow[]> {
  return apiGetPublic<LeaderboardRow[]>(`/play/${encodeURIComponent(slug)}/leaderboard?limit=${limit}`);
}

/**
 * Fetch a published project's uploaded asset manifest (GLB models, images,
 * audio placed in the map). Same wire shape as the authenticated
 * `GET /projects/{id}/assets` (see `AssetWire`/`assetFromWire` in
 * projectRepository), so both feed the same mapper.
 */
export function getPlayAssets(slug: string): Promise<AssetWire[]> {
  return apiGetPublic<AssetWire[]>(`/play/${encodeURIComponent(slug)}/assets`);
}

/**
 * Fetch a published project's mode document (hud/audio/gamedata/logic/etc).
 * Mode documents are not snapshotted per publication version - this always
 * serves the project's current document, matching the internal endpoint the
 * realtime tier uses. A mode never saved resolves to an empty `data: {}`
 * rather than rejecting, same as the authenticated editor read.
 */
export function getPlayDoc<T extends Record<string, unknown> = Record<string, unknown>>(
  slug: string,
  kind: ModeDocKind,
): Promise<{ kind: ModeDocKind; data: T; updated_at: string | null }> {
  return apiGetPublic(`/play/${encodeURIComponent(slug)}/docs/${kind}`);
}

/**
 * Minimal structural check that a publication's map_data is a renderable
 * MapDocument before it is handed to the engine runtime.
 */
export function isPlayableMap(data: unknown): data is MapDocument {
  if (!data || typeof data !== 'object') return false;
  const d = data as Partial<MapDocument>;
  return Array.isArray(d.floors) && Array.isArray(d.objectInstances);
}
